/**
 * **상품 가격 표기** — 통화를 알고 쓴다.
 *
 * 판매몰 상품이 원화 하나였을 때는 화면마다 `` `${n.toLocaleString('ko-KR')}원` `` 한 줄이면
 * 됐다. 아마존(`source: 'amazon'`)이 들어오면서 **달러가 섞였고**, 그 한 줄이 $9.97 을
 * 「10원」으로 보이게 한다. 그래서 표기를 여기 하나로 모은다.
 *
 * ⚠️ **환산하지 않는다.** 달러는 달러로, 원화는 원화로 적는다.
 * 환율을 끌어오면 「언제 환율인가」가 화면마다 달라지고, 그 값은 지금 수집분에 없다.
 *
 * ## ⚠️ 정수 단위(major)와 최소 단위(minor)가 둘 다 온다
 * 아마존 수집분은 `salePrice: 10` 과 `salePriceMinor: 997` 을 함께 싣는데,
 * **major 쪽이 달러를 정수로 반올림한 값**이다($9.97 이 10 으로 들어온다).
 * 그래서 값을 고르는 규칙도 여기 둔다 — `resolveMoneyAmount`.
 * 원화는 최소 단위가 곧 정수라 둘이 같고, 규칙을 태워도 결과가 안 바뀐다.
 *
 * 소비처: `sections/market`(상품정보조사) · `sections/trend-search`(통합 트렌드 검색).
 * 둘이 각자 `formatPrice` 를 갖고 있던 것을 여기로 모았다 — 원화 표기는 글자까지 같고,
 * 달러 표기만 `9.97 USD` 에서 `$9.97` 로 바뀐다(그쪽은 실렸던 적 없는 갈래다).
 */

/**
 * 통화별 소수 자릿수(ISO 4217 minor unit).
 *
 * ⚠️ `KR` 은 오타가 아니다 — 화해 수집분 100건이 `currency: 'KR'` 로 들어와 있다
 * 원화로 읽는다.
 */
const CURRENCY_DECIMALS: Record<string, number> = {
  KRW: 0,
  KR: 0,
  JPY: 0,
  VND: 0,
  USD: 2,
  EUR: 2,
  GBP: 2,
};

/** 원화로 적는 코드 — 기호가 아니라 「원」을 뒤에 붙인다 */
const WON_CODES = new Set(['KRW', 'KR']);

/** 코드 앞에 붙는 기호. 없으면 코드를 뒤에 적는다 */
const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
};

const normalizeCurrency = (currency: unknown): string =>
  String(currency ?? '').trim().toUpperCase() || 'KRW';

/** 모르는 통화는 소수 두 자리로 본다 — 세상 대부분이 그렇다 */
export function currencyDecimals(currency: unknown): number {
  return CURRENCY_DECIMALS[normalizeCurrency(currency)] ?? 2;
}

/**
 * 정수 단위·최소 단위 둘 중 **정확한 쪽**을 골라 정수 단위 금액으로 돌려준다.
 *
 * ⚠️ 최소 단위가 있으면 그쪽이 이긴다 — 위 머리말의 반올림 때문이다.
 * 소수 자릿수가 0인 통화(원·엔)는 나눗셈이 항등이라 어느 쪽을 골라도 같다.
 *
 * 값이 없거나 0 이하면 `null` — 이 저장소는 **0을 「미제공」으로 읽는다**
 * (화해에 `salePrice: 0` 이 실재한다).
 */
export function resolveMoneyAmount(
  major: unknown,
  minor: unknown,
  currency: unknown,
): number | null {
  const decimals = currencyDecimals(currency);
  const minorNum = Number(minor);
  if (Number.isFinite(minorNum) && minorNum > 0) {
    return minorNum / 10 ** decimals;
  }
  const majorNum = Number(major);
  return Number.isFinite(majorNum) && majorNum > 0 ? majorNum : null;
}

/**
 * 문서 하나의 통화·정가·판매가 — 상품 색인을 읽는 매퍼가 모두 이것으로 고른다.
 *
 * ⚠️ 아마존 랭킹 수집분 일부는 **원화 금액에 `currency: 'USD'` 를 붙여** 싣는다(정수 61700 · 최소 단위 6170000).
 * 달러인데 최소 단위가 정확히 정수×100 이고 정수가 `WON_AS_DOLLAR_MIN` 이상이면 원화로 읽는다 —
 * 진짜 달러 값은 정수가 반올림이라 최소 단위와 이렇게 맞지 않는다(10 · 988). 금액은 어느 쪽으로 읽어도 같다.
 * @param src 색인 문서(`salePrice`·`salePriceMinor`·`originalPrice`·`originalPriceMinor`·`currency`)
 * @returns 통화가 비면 `KRW`
 */
export function resolveMoneyFields(src: Record<string, unknown> | null | undefined): {
  currency: string;
  originalPrice: number | null;
  salePrice: number | null;
} {
  const given = String(src?.currency ?? '').trim() || 'KRW';
  const originalPrice = resolveMoneyAmount(src?.originalPrice, src?.originalPriceMinor, given);
  const salePrice = resolveMoneyAmount(src?.salePrice, src?.salePriceMinor, given);
  const currency = isWonLabelledDollar(src, given) ? 'KRW' : given;
  return { currency, originalPrice, salePrice };
}

/**
 * 가격 한 줄.
 *
 * ⚠️ **0 이하·숫자 아님은 빈 문자열이다** — 부르는 쪽이 「값이 없다」로 읽고 자리를 지운다.
 * 두 화면의 옛 `formatPrice` 가 같은 규약이었고 그대로 옮겼다.
 *
 * ```
 *   29640, 'KRW'  →  29,640원
 *    9.97, 'USD'  →  $9.97
 *    1234, 'XYZ'  →  1,234.00 XYZ
 * ```
 */
export function formatPrice(value: unknown, currency: unknown = 'KRW'): string {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';

  const code = normalizeCurrency(currency);
  const decimals = currencyDecimals(code);
  const text = n.toLocaleString('ko-KR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  if (WON_CODES.has(code)) return `${text}원`;
  const symbol = CURRENCY_SYMBOL[code];
  return symbol ? `${symbol}${text}` : `${text} ${code}`;
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 이 정수 이상의 「달러」는 원화로 의심한다 — 판매몰 뷰티 상품에 천 달러 넘는 값은 없다 */
const WON_AS_DOLLAR_MIN = 1000;

/** 원화 금액에 달러 표가 붙었나 — 판매가(없으면 정가)의 정수·최소 단위로 가른다 */
function isWonLabelledDollar(src: Record<string, unknown> | null | undefined, currency: string): boolean {
  if (normalizeCurrency(currency) !== 'USD') return false;
  const sale = Number(src?.salePrice);
  const useSale = Number.isFinite(sale) && sale > 0;
  const major = useSale ? sale : Number(src?.originalPrice);
  const minor = Number(useSale ? src?.salePriceMinor : src?.originalPriceMinor);
  return Number.isFinite(major) && major >= WON_AS_DOLLAR_MIN && Number.isInteger(major) && minor === major * 100;
}
